// ── APP SHELL (P39) — tiny entry chunk ───────────────────────────────────────
// Route-level code splitting by audience:
//   · public/customer pages (/offer, /sign-pi, /product, /showroom) load the
//     slim `pages/public` chunk — WITHOUT the admin app
//   · everything behind login lazily loads the admin chunk (./App)
// While any chunk loads: branded Opaline spinner (green on cream) — never a
// blank page. Chunk-load failures retry once automatically, then show a
// friendly tap-to-retry screen.
import { lazy, Suspense, useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './lib/firebase';
import { G } from './globalCss';

// Branded loading state — must stay trivially small (rendered from the entry chunk).
// P40 anti-flicker: the cream background paints immediately (never blank white),
// but the spinner itself appears only after 150ms — fast loads never flash it.
// (The companion minimum-visible rule lives in lazyRetry below.)
function OpalineSpinner() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 150);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{ minHeight: '100vh', background: '#F8F4ED', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 18 }}>
      {show && <>
        <style>{'@keyframes opspin{to{transform:rotate(360deg)}}'}</style>
        <div style={{ width: 44, height: 44, border: '3px solid rgba(26,58,46,0.15)', borderTopColor: '#1A3A2E', borderRadius: '50%', animation: 'opspin 0.9s linear infinite' }} aria-label="Loading"/>
        <div style={{ fontFamily: 'Georgia,serif', fontSize: 15, letterSpacing: '0.14em', color: '#1A3A2E', textTransform: 'uppercase' }}>Opaline Surfaces</div>
      </>}
    </div>
  );
}

// Friendly dead-end for a chunk that failed twice (flaky connection).
function ChunkFailScreen() {
  return (
    <div style={{ minHeight: '100vh', background: '#F8F4ED', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, padding: 24, textAlign: 'center' }}>
      <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, color: '#1A3A2E' }}>Connection hiccup</div>
      <div style={{ fontFamily: 'Arial,sans-serif', fontSize: 14, color: 'rgba(42,42,42,0.7)', maxWidth: 360, lineHeight: 1.6 }}>
        We couldn't load this page — your connection may have dropped for a moment.
      </div>
      <button onClick={() => window.location.reload()}
        style={{ padding: '14px 30px', background: '#1A3A2E', color: '#F8F4ED', border: 'none', borderRadius: 4, fontFamily: 'Arial,sans-serif', fontSize: 13, letterSpacing: '0.18em', textTransform: 'uppercase', cursor: 'pointer' }}>
        Tap to retry
      </button>
    </div>
  );
}

// lazy() with one automatic retry (1.5s pause), then the retry screen.
// P40 anti-flicker: if the chunk resolves in the 150–450ms window (spinner just
// became visible), hold resolution until 450ms so the spinner shows for a
// minimum of 300ms instead of blinking.
const lazyRetry = (importer) => lazy(() => {
  const t0 = Date.now();
  const settle = (m) => {
    const dt = Date.now() - t0;
    if (dt > 150 && dt < 450) return new Promise(r => setTimeout(() => r(m), 450 - dt));
    return m;
  };
  return importer().then(settle).catch(() =>
    new Promise(r => setTimeout(r, 1500))
      .then(importer)
      .then(settle)
      .catch(() => ({ default: ChunkFailScreen }))
  );
});

const CustomerOfferPage = lazyRetry(() => import('./pages/public').then(m => ({ default: m.CustomerOfferPage })));
const PISigningPage     = lazyRetry(() => import('./pages/public').then(m => ({ default: m.PISigningPage })));
const ShowroomPage      = lazyRetry(() => import('./pages/public').then(m => ({ default: m.ShowroomPage })));
const PublicProductPage = lazyRetry(() => import('./pages/public').then(m => ({ default: m.PublicProductPage })));
const AnnouncementViewPage = lazyRetry(() => import('./pages/public').then(m => ({ default: m.AnnouncementViewPage })));
const AdminRoot         = lazyRetry(() => import('./App'));
const LoginPage         = lazyRetry(() => import('./App').then(m => ({ default: m.LoginPage })));

// P46: admin page paths (kept in sync with ADMIN_PAGES in src/App.js). A
// signed-out hit on one of these shows the login form at that URL instead of
// the public showroom, so post-login lands on the requested page.
// 'vendor-checkins' stays ONLY as an auth-gate segment: the screen is gone
// (P51) and AdminApp redirects it to /vendors after login.
const ADMIN_PATH_SEGS = ['home','catalogue','customers','leads','vendors','build','offers','invoicing','containers','inquiries','calculator','settings','audit','vendor-checkins'];
const isAdminPath = (pathname) => ADMIN_PATH_SEGS.includes((pathname || '').split('/')[1] || '');

export default function AppShell() {
  const [authStatus, setAuthStatus] = useState('loading'); // "loading" | "in" | "out"
  const [authUser, setAuthUser] = useState(null);

  // Inject global CSS once (shared by both audiences — moved from App())
  useEffect(() => {
    const s = document.createElement('style');
    s.textContent = G;
    document.head.appendChild(s);
    return () => document.head.removeChild(s);
  }, []);

  // Track Firebase auth state
  // An open, signed-in app is never torn down by a brief "no user" (e.g. another
  // tab syncing the shared sign-in): the tab must STAY signed out for 2.5s before
  // it switches to the sign-in page — so a blip can't wipe a half-filled form.
  // Pressing "Sign out" is still immediate — in this tab (window.__opSigningOut)
  // and in every other open tab (the shared 'op_signout_at' signal).
  const statusRef = useRef('loading');
  useEffect(() => {
    let outTimer = null;
    const goOut = () => { clearTimeout(outTimer); outTimer = null; setAuthUser(null); statusRef.current = 'out'; setAuthStatus('out'); };
    const signedOutOnPurpose = () => { if (window.__opSigningOut) return true; try { return Date.now() - Number(localStorage.getItem('op_signout_at') || 0) < 15000; } catch { return false; } };
    const unsub = onAuthStateChanged(auth, user => {
      clearTimeout(outTimer); outTimer = null;
      if (user) { window.__opSigningOut = false; setAuthUser(user); statusRef.current = 'in'; setAuthStatus('in'); return; }
      if (statusRef.current === 'in' && !signedOutOnPurpose()) outTimer = setTimeout(goOut, 2500);
      else goOut();
    });
    // "Sign out" pressed in another tab: leave at once — don't wait for the
    // shared session to report it (that can trail by seconds on Safari)
    const onStorage = (e) => { if (e.key === 'op_signout_at' && e.newValue && statusRef.current === 'in') goOut(); };
    window.addEventListener('storage', onStorage);
    // "Sign out" pressed in THIS tab: leave at once (WebKit can take seconds to finish the sign-out)
    const onSignOutNow = () => { if (statusRef.current === 'in') goOut(); };
    window.addEventListener('op-signout-now', onSignOutNow);
    return () => { clearTimeout(outTimer); unsub(); window.removeEventListener('storage', onStorage); window.removeEventListener('op-signout-now', onSignOutNow); };
  }, []);

  // P40 idle prefetch: once auth settles, quietly warm the chunk the user is
  // most likely to need next (admins → the public showroom chunk for "View
  // Showroom"; visitors on /login → the admin chunk so post-login is instant;
  // note the /login route itself already lives in the admin chunk, so typing
  // credentials naturally preloads it). Cached by the browser — one-time cost.
  useEffect(() => {
    if (authStatus === 'loading') return;
    const t = setTimeout(() => {
      import('./pages/public').catch(() => {});
      if (authStatus === 'in' || window.location.pathname === '/login') import('./App').catch(() => {});
    }, 2500);
    return () => clearTimeout(t);
  }, [authStatus]);

  return (
    <BrowserRouter>
      <Suspense fallback={<OpalineSpinner/>}>
        <Routes>
          {/* Customer links — always public, no auth required, slim chunk */}
          <Route path="/offer/:offerId" element={<CustomerOfferPage/>}/>
          <Route path="/sign-pi/:token" element={<PISigningPage/>}/>
          <Route path="/showroom"       element={<ShowroomPage/>}/>
          <Route path="/product/:id"    element={<PublicProductPage/>}/>
          <Route path="/a/:id"          element={<AnnouncementViewPage/>}/>

          {/* Explicit login route — unauth shows form, auth redirects to admin home */}
          <Route path="/login" element={
            authStatus === 'loading' ? <OpalineSpinner/> :
            authStatus === 'in'      ? <Navigate to="/" replace/> :
                                       <LoginPage/>
          }/>

          {/* Root + admin — unauth visitors see the public showroom; admins
              see the AdminApp. /showroom above still works for admins too.
              P46: a direct admin URL (/catalogue, /offers, …) hit while signed
              out shows the login form AT THAT URL — when auth flips to 'in'
              this same route re-renders AdminRoot on the same pathname, so you
              land on the requested page, not Home. Unknown paths for visitors
              keep showing the showroom exactly as before. */}
          <Route path="/*" element={
            authStatus === 'loading' ? <OpalineSpinner/> :
            authStatus === 'in'      ? <AdminRoot userEmail={authUser?.email}/> :
            isAdminPath(window.location.pathname) ? <LoginPage/> :
                                       <ShowroomPage/>
          }/>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
